import { useEffect, useMemo, useState } from "react";
import { useContentStore, CONTENT_STAGES, EMPTY_ITEMS, type ContentStage, type ContentItem } from "@/stores/contentStore";
import { ContentCard } from "./ContentCard";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

const ACTIVE_STAGES = new Set<string>(["researching", "outlining", "writing"]);
const POLL_INTERVAL_MS = 5000;

interface ContentKanbanProps {
  projectId: number;
}

export function ContentKanban({ projectId }: ContentKanbanProps) {
  const items = useContentStore((s) => s.items[projectId] ?? EMPTY_ITEMS);
  const moveItemToStage = useContentStore((s) => s.moveItemToStage);
  const selectItem = useContentStore((s) => s.selectItem);
  const { toast } = useToast();

  const itemsByStage = useMemo(() => {
    const map: Record<string, ContentItem[]> = {};
    for (const stage of CONTENT_STAGES) map[stage.id] = [];
    for (const item of items) {
      if (map[item.stage]) {
        map[item.stage].push(item);
      } else if (item.stage === "error" || item.stage === "killed") {
        // Show error/killed items in "scheduled" column so they remain visible
        map["scheduled"].push(item);
      }
    }
    return map;
  }, [items]);

  // Poll active items every 5 seconds
  const refreshItem = useContentStore((s) => s.refreshItem);
  const hasActive = useMemo(() => {
    return items.some((i) => ACTIVE_STAGES.has(i.stage));
  }, [items]);

  useEffect(() => {
    if (!hasActive) return;

    const poll = () => {
      const currentItems = useContentStore.getState().items[String(projectId)] || [];
      currentItems
        .filter((i) => ACTIVE_STAGES.has(i.stage))
        .forEach((item) => refreshItem(item.id, projectId));
    };

    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasActive, projectId, refreshItem]);

  const [dragOverStage, setDragOverStage] = useState<ContentStage | null>(null);

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    e.dataTransfer.setData("contentItemId", itemId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => setDragOverStage(null);

  const handleDragOver = (e: React.DragEvent, stageId: ContentStage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStage(stageId);
  };

  const handleDragLeave = () => setDragOverStage(null);

  const handleDrop = async (e: React.DragEvent, stage: ContentStage) => {
    e.preventDefault();
    setDragOverStage(null);
    const itemId = e.dataTransfer.getData("contentItemId");
    if (!itemId) return;

    const item = items.find((i) => i.id === itemId);
    if (!item || item.stage === stage) return;

    try {
      await moveItemToStage(itemId, projectId, stage);
    } catch {
      toast({
        title: "Move failed",
        description: "Could not update content item stage",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex gap-4 p-6 overflow-x-auto min-h-0 h-full" role="region" aria-label="Content board">
      {CONTENT_STAGES.map((stage) => {
        const stageItems = itemsByStage[stage.id] || [];
        const isDragOver = dragOverStage === stage.id;

        return (
          <div
            key={stage.id}
            className={cn(
              "flex flex-col min-w-[220px] w-[220px] shrink-0 rounded-[6px] transition-colors duration-150",
              isDragOver && "bg-[rgba(0,178,200,0.04)]"
            )}
            role="group"
            aria-label={`${stage.label} column (${stageItems.length} items)`}
            onDragOver={(e) => handleDragOver(e, stage.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, stage.id)}
          >
            {/* Column header */}
            <div className="flex items-center gap-2 px-2 py-2 mb-2">
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: stage.color }}
              />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {stage.label}
              </span>
              <span className="text-xs text-muted-foreground/60 ml-auto">
                {stageItems.length}
              </span>
            </div>

            {/* Cards */}
            <div className="flex flex-col gap-2 flex-1 min-h-[100px]">
              {stageItems.map((item) => (
                <ContentCard
                  key={item.id}
                  item={item}
                  projectId={projectId}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onClick={() => selectItem(item.id)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
